import { FlightsClassTypes } from 'constants/flight'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Modal from 'LuxKit/Modal/Modal'
import React, { useCallback, useMemo, useState } from 'react'
import FareClassCard from './FareClassCard'
import Caption from 'LuxKit/Typography/Caption'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Breakpoint from 'components/Common/Breakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LinePlayCircleIcon from 'LuxKit/Icons/line/LinePlayCircleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineClochIcon from 'LuxKit/Icons/line/LineClochIcon'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { useFareClassPriceDifferencesPerAdult } from 'hooks/CharterFlights/useFareClassPriceDifferences'
import { EmptyArray } from 'lib/array/arrayUtils'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'

const imageIds = {
  [FlightsClassTypes.ECONOMY]: 'maldives-charter-economy',
  [FlightsClassTypes.PREMIUM]: 'maldives-charter-premium-economy',
  [FlightsClassTypes.LUXROW]: 'luxrow_fare_select_maldives_charter',
  [FlightsClassTypes.BUSINESS]: 'maldives-charter-business',
} as const

// All fare classes available in the modal (excluding LUXROW for now)
const allFareClasses = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.BUSINESS,
] as const

type BaggageClass = FlightsClassTypes.ECONOMY | FlightsClassTypes.PREMIUM | FlightsClassTypes.LUXROW | FlightsClassTypes.BUSINESS

// Hardcoded baggage values for Maldives charter - do not use API values
const MALDIVES_CHARTER_BAGGAGE: Record<BaggageClass, { carryOn: string, checked: string }> = {
  [FlightsClassTypes.ECONOMY]: { carryOn: 'Carry-on 7kg', checked: 'Checked bag 23kg' },
  [FlightsClassTypes.PREMIUM]: { carryOn: 'Carry-on 7kg', checked: 'Checked bag 23kg' },
  [FlightsClassTypes.LUXROW]: { carryOn: 'Carry-on 7kg', checked: 'Checked bag 23kg' },
  [FlightsClassTypes.BUSINESS]: { carryOn: 'Carry-on 2 pieces up to 14kg, max 10kg per piece', checked: 'Checked bag 2 pieces, total 46kg, no more than 32kg per piece' },
}

interface Props {
  checkInDate?: string
  checkOutDate?: string
  occupancies?: Array<App.Occupants>
  selectedClassId: FlightsClassTypes
}

/**
 * This is specific to the Maldives charter flights. It's not designed to be reusable for future charter flight offers.
 * Uses modal's resolve mechanism to return the selected fare class.
 */
function MaldivesCharterFlightFareClassModal({ selectedClassId, checkInDate, checkOutDate, occupancies = EmptyArray }: Props) {
  const modalElement = useModalElementContext<FlightsClassTypes>()

  // Use local state to track selection within the modal
  const [localSelectedClass, setLocalSelectedClass] = useState<FlightsClassTypes>(selectedClassId)

  const onClassSelect = useCallback((fareClass: FlightsClassTypes) => {
    setLocalSelectedClass(fareClass)
  }, [])

  const closeModal = useCallback(() => {
    // Return the selected class via modal's resolve mechanism
    modalElement.resolve(localSelectedClass)
  }, [modalElement, localSelectedClass])

  const _charterFlightData = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: localSelectedClass,
  })

  // Calculate price differences relative to the currently selected class (before modal opened)
  const comparisonClasses = useMemo(
    () => allFareClasses.filter(c => c !== selectedClassId),
    [selectedClassId],
  )

  const priceDifferences = useFareClassPriceDifferencesPerAdult({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    baseClass: selectedClassId,
    comparisonClasses,
    checkInDate,
    checkOutDate,
    occupancies,
    selectedFareClass: selectedClassId,
  })

  const economyPriceDiff = priceDifferences[FlightsClassTypes.ECONOMY]
  const premiumPriceDiff = priceDifferences[FlightsClassTypes.PREMIUM]
  const businessPriceDiff = priceDifferences[FlightsClassTypes.BUSINESS]
  // const luxRowPriceDiff = priceDifferences[FlightsClassTypes.LUXROW]

  const renderFareClassSubtitle = useCallback((fareClass: FlightsClassTypes, priceDiff: number | undefined) => {
    if (fareClass === FlightsClassTypes.ECONOMY && selectedClassId === FlightsClassTypes.ECONOMY) {
      return <Caption variant="large" weight="normal" colour="neutral-two">Included in bundle</Caption>
    }
    if (selectedClassId === fareClass) {
      return undefined
    }
    if (priceDiff === undefined) {
      return undefined
    }
    return (
      <Caption variant="large" weight="normal" colour="neutral-two">
        <Group direction="horizontal" gap={4} verticalAlign="baseline">
          <PriceRowCaption>From</PriceRowCaption>
          <PriceRowPrice price={priceDiff} saleUnit="person" size="S" signDisplay="always" />
        </Group>
      </Caption>
    )
  }, [selectedClassId])

  // Use hardcoded baggage values - do not use API values
  const getBaggageCopy = useCallback((fareClass: BaggageClass) => MALDIVES_CHARTER_BAGGAGE[fareClass], [])

  const economyBaggageCopy = getBaggageCopy(FlightsClassTypes.ECONOMY)
  const premiumBaggageCopy = getBaggageCopy(FlightsClassTypes.PREMIUM)
  // const luxRowBaggageCopy = getBaggageCopy(FlightsClassTypes.LUXROW)
  const businessBaggageCopy = getBaggageCopy(FlightsClassTypes.BUSINESS)

  const cards = <>
    <FareClassCard
      fareId={FlightsClassTypes.ECONOMY}
      imageId={imageIds[FlightsClassTypes.ECONOMY]}
      name="Economy"
      subtitle={renderFareClassSubtitle(FlightsClassTypes.ECONOMY, economyPriceDiff)}
      isSelected={localSelectedClass === FlightsClassTypes.ECONOMY}
      onSelect={onClassSelect}
    >
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>Main cabin standard seat</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>Stream on-demand entertainment to your personal device</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>Complimentary meal and drinks service</CaptionTextBlock>
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          <BodyText variant="small" weight="bold">Baggage</BodyText>
          <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{economyBaggageCopy.carryOn}</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{economyBaggageCopy.checked}</CaptionTextBlock>
        </VerticalSpacer>
      </VerticalSpacer>
    </FareClassCard>

    <FareClassCard
      fareId={FlightsClassTypes.PREMIUM}
      imageId={imageIds[FlightsClassTypes.PREMIUM]}
      name="Economy Plus"
      subtitle={renderFareClassSubtitle(FlightsClassTypes.PREMIUM, premiumPriceDiff)}
      isSelected={localSelectedClass === FlightsClassTypes.PREMIUM}
      onSelect={onClassSelect}
    >
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>Extra legroom and generous seat recline for enhanced comfort</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>Stream on-demand entertainment to provided handheld entertainment screens</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>Complimentary meal and drinks service</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineTicketIcon />}>Priority boarding</CaptionTextBlock>
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          <BodyText variant="small" weight="bold">Baggage</BodyText>
          <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{premiumBaggageCopy.carryOn}</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{premiumBaggageCopy.checked}</CaptionTextBlock>
        </VerticalSpacer>
      </VerticalSpacer>
    </FareClassCard>

    {/* <FareClassCard
      fareId={FlightsClassTypes.LUXROW}
      imageId={imageIds[FlightsClassTypes.LUXROW]}
      name="Lux Row"
      subtitle={!!luxRowExtraCost && <Caption
        variant="large"
        weight="normal"
        colour="neutral-two"
      >
        <Group direction="horizontal" gap={4} verticalAlign="baseline">
          <PriceRowCaption>From</PriceRowCaption>
          <PriceRowPrice price={luxRowExtraCost} saleUnit="person" size="S" signDisplay="always" />
        </Group>
      </Caption>}
      isSelected={localSelectedClass === FlightsClassTypes.LUXROW}
      onSelect={onClassSelect}
    >
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>Enjoy more space for two passengers to unwind with a row of four economy seats for the price of three.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>Stream on-demand entertainment to provided handheld entertainment screens</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>Complimentary meal and drinks service</CaptionTextBlock>
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          <BodyText variant="small" weight="bold">Baggage</BodyText>
          <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{luxRowBaggageCopy.carryOn}</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{luxRowBaggageCopy.checked}</CaptionTextBlock>
        </VerticalSpacer>
      </VerticalSpacer>
    </FareClassCard> */}

    <FareClassCard
      fareId={FlightsClassTypes.BUSINESS}
      imageId={imageIds[FlightsClassTypes.BUSINESS]}
      name="Business"
      subtitle={renderFareClassSubtitle(FlightsClassTypes.BUSINESS, businessPriceDiff)}
      isSelected={localSelectedClass === FlightsClassTypes.BUSINESS}
      onSelect={onClassSelect}
    >
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>Luxury lie-flat seats with in-seat power for ultimate comfort and convenience</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>Stream on-demand entertainment to provided handheld entertainment screens</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>Gourmet dining options and drinks service</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineTicketIcon />}>Priority boarding</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineClochIcon />}>Dedicated cabin crew for personalised service</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineDoorIcon />}>Relax with airport lounge before your flight</CaptionTextBlock>
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          <BodyText variant="small" weight="bold">Baggage</BodyText>
          <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{businessBaggageCopy.carryOn}</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{businessBaggageCopy.checked}</CaptionTextBlock>
        </VerticalSpacer>
      </VerticalSpacer>
    </FareClassCard>
  </>

  return <Modal
    title="Select a class"
    subtitle="Maldivian · Airbus A330-200"
    primaryActionText="Continue"
    onPrimaryActionClick={closeModal}
    onClose={closeModal}
    size="XL"
    height="full"
    tabletHeight="auto"
  >
    <Breakpoint max="tablet">
      <CardCarousel gap={12} snap="start" gutterSize={20}>
        {cards}
      </CardCarousel>
    </Breakpoint>

    <Breakpoint min="desktop">
      <Group direction="horizontal" gap={12} verticalAlign="stretch" horizontalAlign="stretch">
        {cards}
      </Group>
    </Breakpoint>
  </Modal>
}

export default MaldivesCharterFlightFareClassModal
